import React, { useEffect, useState } from 'react';
import {
  DatePicker,
  Form,
  Input,
  Button,
  Modal,
  Select,
  Slider,
  Row,
  Col,
  Tooltip,
  Alert,
  Space,
  Switch,
  Divider,
  AutoComplete,
} from 'antd';

import { withAuth, AuthContextProps } from '../LocalAuthWrapper';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CAPTION_RTMP_API_URL } from '../config';

import customParseFormat from 'dayjs/plugin/customParseFormat';

import './CreateBroadcastForm.css';
import { StreamKey } from '../types';
import { RTC_MAX_BROADCAST_LENGTH, RTC_MIN_BROADCAST_LENGTH } from '../Constants';
import { isFeatureEnabled } from '../features';
import RTCStreamInfoCopier from './RTCStreamInfoCopier';
import { useGetStreamKeys } from '../hooks/useGetStreamKeys';

dayjs.extend(customParseFormat);

type CreateBroadcastFormProps = {
  userId: string;
  show: boolean;
  toggleShow: () => boolean;
} & AuthContextProps;

// eslint-disable-next-line max-lines-per-function
const CreateBroadcastForm: React.FC<CreateBroadcastFormProps> = (props) => {
  const { show } = props;

  const [startNow, setStartNow] = useState<boolean>(false);
  const [selectedDate, setSelectDate] = useState(dayjs().date());
  const [form] = Form.useForm();
  const tiroRtmpKeyFormVal = Form.useWatch('tiroRtmpKey', form);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const streamKeys = useGetStreamKeys(props.userId, props.authCtx.accessToken!, [
    props.userId,
    props.authCtx.accessToken!,
    show,
  ]);
  const [overwriteable, setOverwriteable] = useState<boolean>(false);

  const toggleShow = () => {
    props.toggleShow();
    form.setFieldValue('tiroRtmpKey', null);
  };

  /* The emails field is temporily removed until the forwarding/sharing implementation has been implemented in the backend. */
  /* We don't need this function at the moment. */
  // const validateEmails = (emails: string[] | any) => {
  //     let emailsList: string[] = [];
  //     // Split string by comma into an array{
  //     var regex = /[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/;

  //     emails.forEach((email: string) => {
  //         // Trim whitespaces from email address
  //         if (email === undefined) {
  //             emails.pop(email)
  //         }
  //         // Check email against our regex to determine if email is valid
  //         if (email === "" || !regex.test(email) || email === undefined) {
  //             console.error("Email is not valid or empty.");
  //             return
  //         } else {
  //             email.trim();
  //             emailsList.push(email);
  //         }
  //     })

  //     return emailsList;
  // }

  const concatDestinationAndRtmpKey = (destination: string, rtmpKey: string) => {
    if (destination.charAt(destination.length - 1) === '/') {
      return destination + rtmpKey;
    }
    return destination + '/' + rtmpKey;
  };

  const createDataJson = (values: any) => {
    try {
      // let emailsList = validateEmails(values.emails); See comment below.

      return {
        display_name: values.displayName,
        destination: concatDestinationAndRtmpKey(values.destination, values.destinationRtmpKey),
        timeframe: {
          ...(!startNow && { start_time: values.estStartTime.format('YYYY-MM-DD HH:mm').toString() + ':00' }),
          end_time: !startNow
            ? values.estStartTime.add(values.duration, 'minute').format('YYYY-MM-DD HH:mm').toString() + ':00'
            : dayjs().add(values.duration, 'minute').format('YYYY-MM-DD HH:mm').toString() + ':00',
        },
        fwd_emails: [], // TODO: Use emailsList when the emails field is reintroduced after backend implementation.
        stream_key_public_id: values.tiroRtmpKey,
      };
    } catch (err) {
      console.error(err);
    }
  };

  const onFinish = (values: any) => {
    form
      .validateFields()
      .then((values) => {
        const data = createDataJson(values);
        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${props.authCtx.accessToken}`,
          },
          body: JSON.stringify(data),
        };

        fetch(`${CAPTION_RTMP_API_URL}/broadcasts/${props.userId}`, requestOptions)
          .then(async (response) => {
            const isJson = response.headers.get('content-type')?.includes('application/json');
            const data = isJson && (await response.json());
            // check for error response
            if (!response.ok) {
              // get error message from body or default to response status
              const error = (data && data.message) || response.status;
              const errorMsg = data.message;
              if (errorMsg) {
                setError(errorMsg);
              } else {
                setError(data.error.message);
              }
              setErrorCode(response.status.toString());
              return Promise.reject(error);
            }
            console.log('Rauntímatextunarbeiðni send inn!');
            form.resetFields();
            setStartNow(false);
            toggleShow();

            if (isFeatureEnabled('REAL_TIME_CAPTIONING_BROADCAST_OVERWRITE') && overwriteable) {
              fetch(`${CAPTION_RTMP_API_URL}/broadcasts/${data.id}/actions/set_flags?overwritable=true`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  authorization: `Bearer ${props.authCtx.accessToken}`,
                },
              })
                .then(async (response) => {
                  const flagData = await response.json();
                  console.log(flagData);
                })
                .catch((error) => {
                  console.error(error);
                });
            }
          })
          .catch((error) => {
            console.error('There was an error!', error.log);
          });
      })
      .catch((info) => {
        console.info('Validate Failed:', info);
      });
  };

  const onSelect = (date: any) => {
    if (date === dayjs()) {
      setSelectDate(dayjs().date());
      return;
    }
    setSelectDate(date.date());
  };

  const disabledDate = (current: any) => {
    return current && current < dayjs().endOf('days').subtract(1, 'days');
  };

  const disabledHours = () => {
    const hours = [];
    const currentHour = dayjs().hour();
    if (selectedDate === dayjs().date()) {
      for (let i = currentHour - 1; i >= 0; i--) {
        hours.push(i);
      }
    }
    return hours;
  };

  const disabledMinutes = (selectedHour: any) => {
    const minutes = [];
    const currentMinute = dayjs().minute();
    if (selectedHour === dayjs().hour() && selectedDate === dayjs().date()) {
      for (let i = currentMinute; i >= 0; i--) {
        minutes.push(i);
      }
    }
    return minutes;
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const handleCancel = () => {
    toggleShow();
    setErrorCode('');
    setError('');
  };

  const autoCompleteDestinations: { value: string }[] = [
    { value: 'rtmps://live-api-s.facebook.com:443/rtmp' },
    { value: 'rtmps://rtmp-global.cloud.vimeo.com:443/live' },
    { value: 'rtmp://a.rtmp.youtube.com/live2' },
  ];

  return (
    <Modal
      open={show}
      onCancel={handleCancel}
      okText='Búa til'
      cancelText='Hætta við'
      onOk={onFinish}
      width={1000}
      centered
    >
      <h3>Skrá nýja útsendingu</h3>
      {!!error && (
        <Space direction='vertical' style={{ margin: '1rem' }}>
          <Alert
            message='Það kom upp villa!'
            description={`Status code: ${errorCode} - ${error}`}
            type='error'
            showIcon
          />
        </Space>
      )}
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        id='rtc-create-form'
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 12 }}
        style={{ width: '100%' }}
        initialValues={{ emails: [], estStartTime: dayjs().add(10, 'minute') }}
      >
        <Divider orientation='left'>Grunnupplýsingar</Divider>
        <Form.Item
          name='displayName'
          label='Heiti'
          rules={[
            {
              type: 'string',
              required: true,
              message: 'Vantar nafn á útsendingu!',
              whitespace: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label='Upphafstímasetning' style={{ marginBottom: 0 }}>
          <Space>
            <Form.Item>
              <Tooltip title={startNow ? 'Útsending hefst strax' : 'Útsending hefst seinna'}>
                <Switch
                  checkedChildren='Núna'
                  unCheckedChildren='Seinna'
                  checked={startNow}
                  onClick={() => setStartNow(!startNow)}
                />
              </Tooltip>
            </Form.Item>
            {isFeatureEnabled('REAL_TIME_CAPTIONING_BROADCAST_OVERWRITE') && (
              <Form.Item>
                <Tooltip
                  title={overwriteable ? 'Útsending verður yfirskrifanleg' : 'Útsending verður ekki yfirskrifanleg'}
                >
                  <Switch
                    checkedChildren='Yfirskrifanleg'
                    unCheckedChildren='Ekki yfirskrifanleg'
                    checked={overwriteable}
                    onClick={() => setOverwriteable(!overwriteable)}
                  />
                </Tooltip>
              </Form.Item>
            )}
            <Form.Item name='estStartTime' rules={[{ required: true, message: 'Vantar tíma!' }]}>
              <DatePicker
                minuteStep={5}
                showTime={{ format: 'HH:mm' }}
                format='YYYY-MM-DD HH:mm'
                style={{ width: '100%' }}
                onSelect={onSelect}
                disabled={startNow}
                disabledDate={disabledDate}
                disabledHours={disabledHours}
                disabledMinutes={disabledMinutes}
                showNow={false}
                allowClear={false}
                inputReadOnly={true}
              />
            </Form.Item>
          </Space>
        </Form.Item>
        <Form.Item
          name='duration'
          label='Tímalengd'
          rules={[{ required: true }]}
          initialValue={RTC_MIN_BROADCAST_LENGTH}
        >
          <Slider
            min={RTC_MIN_BROADCAST_LENGTH}
            max={RTC_MAX_BROADCAST_LENGTH}
            value={RTC_MIN_BROADCAST_LENGTH}
            tooltip={{
              formatter: (val: any) =>
                `${val} mínút${val > 1 ? 'ur' : 'a'} - Útsendingu lýkur: ${dayjs(
                  startNow ? undefined : form.getFieldValue('estStartTime'),
                )
                  .add(val, 'minute')
                  .format('YYYY-MM-DD HH:mm')}`,
            }}
          />
        </Form.Item>
        {false && (
          <Form.List /* The emails field is temporily removed until the forwarding/sharing implementation has been implemented in the backend. */
            name='emails'
          >
            {(fields, { add, remove }) => (
              <>
                {/* TODO: Make first field visible for input without needing to press "Add" button */}
                {fields.map((field, name) => (
                  <Form.Item
                    // {...formItemLayout}
                    label='Viðtakandi'
                    required={false}
                    key={field.key}
                  >
                    <Row>
                      <Col>
                        <Form.Item
                          name={field.name}
                          validateTrigger={['onChange', 'onBlur']}
                          rules={[
                            {
                              required: true,
                              message: 'Bættu við netfangi eða eyddu reitnum',
                            },
                          ]}
                          noStyle
                        >
                          <Input placeholder='Netfang' />
                        </Form.Item>
                      </Col>
                      <Col>
                        <Tooltip title='Fjarlægja viðtakanda'>
                          <Button
                            onClick={() => remove(field.name)}
                            shape='circle'
                            type='dashed'
                            icon={<MinusCircleOutlined className='dynamic-delete-button' />}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Form.Item>
                ))}
                <Form.Item>
                  <Button type='dashed' onClick={() => add()} icon={<PlusCircleOutlined />}>
                    Bæta við viðtakanda
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        )}
        <Divider orientation='left'>Textunarþjónusta</Divider>
        <Form.Item label='Slóð' colon={false}>
          <RTCStreamInfoCopier
            streamKeyVal={streamKeys.find((s) => s.option.value === tiroRtmpKeyFormVal)?.keyVal || ''}
          />
        </Form.Item>
        <Form.Item label='Streymislykill'>
          <Form.Item name='tiroRtmpKey' rules={[{ required: true, message: 'Vantar að velja streymislykil' }]}>
            <Select
              placeholder='Vinsamlegast veljið streymislykil'
              options={[...streamKeys.map((s) => s.option)]}
            ></Select>
          </Form.Item>
        </Form.Item>
        <Divider orientation='left'>Áfangastaður</Divider>
        <Form.Item
          name='destination'
          label='Slóð'
          rules={[
            {
              required: true,
              message: 'Vantar gildan áfangastað',
            },
            {
              pattern:
                /^rtmps?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
              message: 'Vantar áfangastað með gildan rtmp(s)://<hlekk>!',
            },
          ]}
        >
          <AutoComplete
            placeholder='rtmp(s)://...'
            options={autoCompleteDestinations}
            filterOption={(inputValue, option) => option!.value.includes(inputValue)}
          />
        </Form.Item>
        <Form.Item
          name='destinationRtmpKey'
          label='Streymislykill'
          rules={[
            {
              required: true,
              message: 'Vantar gildan RTMP-lykil',
            },
            {
              pattern: /\S*/gm,
              message: 'Vantar RTMP-lykil',
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default withAuth(CreateBroadcastForm);
